import type { Coding } from 'fhir/r4';
import * as yup from 'yup';

import { getSupportedCode } from '../mapping';

/**
 * Validates that only one of the supported codings is supplied
 *
 * @param supportedCodings
 * @param codings
 * @returns
 */
const validateCodings = <T extends string>(
  supportedCodes: T[],
  codings?: Coding[],
): boolean => {
  const supportedCode = getSupportedCode(supportedCodes, codings);

  return !!supportedCode;
};

/**
 * Creates a yup schema for validating the supported codings, enforces that only one of the
 * supported codes are allowed
 *
 * @param supportedCodings
 * @returns
 */
export const createCodingsSchema = <T extends string>(supportedCodes: T[]) => {
  const testName = 'isSupportedCoding';
  // eslint-disable-next-line no-template-curly-in-string
  const errorMessage = '${path} is not a valid coding entry';
  const validator = validateCodings.bind(undefined, supportedCodes);
  return yup.object({
    coding: yup.array().test(testName, errorMessage, validator),
  });
};

/**
 * Creates a yup schema for validating the supported codings, enforces that only one of the
 * supported codes are allowed
 *
 * @param supportedCodes codes of which any in the list are allowed
 * @returns
 */
export const createCodeableConceptSchema = <T extends string>(
  supportedCodes: T[],
) =>
  yup
    .object({
      coding: yup
        .array(
          yup.object({
            code: yup.string().oneOf(supportedCodes).required(),
          }),
        )
        .required(),
    })
    .required();
